import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { extractionAPI, integrationsAPI } from '../../services/api';
import { FileSpreadsheet, Plus, Trash2, Save, X, ExternalLink, AlertTriangle, Contact } from 'lucide-react';
import toast from 'react-hot-toast';

const TYPES = ['string', 'date', 'money', 'number'];

const emptyTemplate = () => ({
  name: '',
  description: '',
  channel: 'telegram',
  classifierHints: [],
  schema: [{ key: '', label: '', type: 'string', required: false, instruction: '' }],
  spreadsheetId: '',
  spreadsheetName: '',
  sheetName: 'Sheet1',
  saveToContacts: true,
  enabled: true,
});

const CONTACTS_SCOPE = 'https://www.googleapis.com/auth/contacts';

export default function ExtractionTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [hintsText, setHintsText] = useState('');
  const [saving, setSaving] = useState(false);

  // Google Sheets state
  const [googleConnected, setGoogleConnected] = useState(null); // null=unknown, true/false
  const [contactsAllowed, setContactsAllowed] = useState(false); // Google grant includes Contacts
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [loadingTabs, setLoadingTabs] = useState(false);

  const load = async () => {
    try {
      const { data } = await extractionAPI.list();
      setTemplates(data.data || []);
    } catch (_) {}
  };

  const checkGoogle = async () => {
    try {
      const { data } = await integrationsAPI.getGoogleStatus();
      // Endpoint returns the GoogleIntegration doc (or null) — truthy = connected
      setGoogleConnected(!!data.data);
      setContactsAllowed(String(data.data?.scope || '').split(/\s+/).includes(CONTACTS_SCOPE));
    } catch (_) { setGoogleConnected(false); }
  };

  useEffect(() => { load(); checkGoogle(); }, []);

  const loadSpreadsheets = async () => {
    setLoadingSheets(true);
    try {
      const { data } = await integrationsAPI.listSpreadsheets();
      setSpreadsheets(data.data || []);
    } catch (_) { toast.error('Failed to load spreadsheets'); }
    finally { setLoadingSheets(false); }
  };

  const loadTabs = async (spreadsheetId) => {
    if (!spreadsheetId) return setTabs([]);
    setLoadingTabs(true);
    try {
      const { data } = await integrationsAPI.listSheetTabs(spreadsheetId);
      setTabs(data.data?.tabs || []);
    } catch (_) { toast.error('Failed to load sheet tabs'); }
    finally { setLoadingTabs(false); }
  };

  const openNew = () => {
    setSelectedId(null);
    setDraft(emptyTemplate());
    setHintsText('');
    setTabs([]);
    if (googleConnected && spreadsheets.length === 0) loadSpreadsheets();
  };

  const openExisting = (t) => {
    setSelectedId(t._id);
    setDraft({
      name: t.name,
      description: t.description || '',
      channel: t.channel,
      classifierHints: t.classifierHints || [],
      schema: t.schema?.length ? t.schema : [{ key: '', label: '', type: 'string', required: false, instruction: '' }],
      spreadsheetId: t.spreadsheetId || '',
      spreadsheetName: t.spreadsheetName || '',
      sheetName: t.sheetName || 'Sheet1',
      saveToContacts: t.saveToContacts !== false,
      enabled: t.enabled,
    });
    setHintsText((t.classifierHints || []).join(', '));
    if (googleConnected && spreadsheets.length === 0) loadSpreadsheets();
    if (t.spreadsheetId) loadTabs(t.spreadsheetId);
  };

  const reconnectGoogle = async () => {
    try {
      const { data } = await integrationsAPI.getGoogleAuthUrl();
      window.location.href = data.data.url;
    } catch (_) {}
  };

  const closeEditor = () => { setDraft(null); setSelectedId(null); };

  const updateField = (idx, key, value) => {
    setDraft(d => ({
      ...d,
      schema: d.schema.map((f, i) => i === idx ? { ...f, [key]: value } : f),
    }));
  };
  const addField = () => setDraft(d => ({ ...d, schema: [...d.schema, { key: '', label: '', type: 'string', required: false, instruction: '' }] }));
  const removeField = (idx) => setDraft(d => ({ ...d, schema: d.schema.filter((_, i) => i !== idx) }));

  const onPickSpreadsheet = (id) => {
    const ss = spreadsheets.find(s => s.id === id);
    setDraft(d => ({ ...d, spreadsheetId: id, spreadsheetName: ss?.name || '', sheetName: '' }));
    loadTabs(id);
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error('Template name required');
    const cleanSchema = draft.schema.filter(f => f.key.trim() && f.label.trim());
    if (cleanSchema.length === 0) return toast.error('Add at least one field');
    if (!draft.spreadsheetId) return toast.error('Pick a Google Sheet to save extracted rows');
    if (!draft.sheetName) return toast.error('Pick a sheet tab');

    const payload = {
      ...draft,
      schema: cleanSchema,
      classifierHints: hintsText.split(',').map(s => s.trim()).filter(Boolean),
    };

    setSaving(true);
    try {
      if (selectedId) {
        const { data } = await extractionAPI.update(selectedId, payload);
        setTemplates(prev => prev.map(t => t._id === selectedId ? data.data : t));
        toast.success('Template updated');
      } else {
        const { data } = await extractionAPI.create(payload);
        setTemplates(prev => [data.data, ...prev]);
        setSelectedId(data.data._id);
        toast.success('Template created');
      }
    } catch (_) {} finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Rows already written to Google Sheet are kept.`)) return;
    try {
      await extractionAPI.delete(t._id);
      setTemplates(prev => prev.filter(x => x._id !== t._id));
      if (selectedId === t._id) closeEditor();
      toast.success('Deleted');
    } catch (_) {}
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Document Extraction</h1>
          <p className="page-subtitle">Telegram image → extract fields → append row to Google Sheet</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}><Plus size={15} /> New Template</button>
      </div>

      {googleConnected === false && (
        <div className="page-body">
          <div className="alert alert-warning" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <AlertTriangle size={18} />
            <div style={{ flex: 1 }}>
              Google Sheets is not connected. Connect it in Integrations to use extraction.
            </div>
            <Link to="/dashboard/integrations" className="btn btn-sm btn-primary">
              Connect Google <ExternalLink size={13} />
            </Link>
          </div>
        </div>
      )}

      <div className="page-body" style={{ display: 'grid', gridTemplateColumns: draft ? '1fr 1.4fr' : '1fr', gap: 16 }}>
        {/* List */}
        <div className="card" style={{ padding: 0 }}>
          {templates.length === 0 ? (
            <div className="empty-state" style={{ padding: 60 }}>
              <FileSpreadsheet size={36} />
              <h3>No templates yet</h3>
              <p>Create a template to extract fields from images into Google Sheets</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr><th>Name</th><th>Destination</th><th>Fields</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t._id}
                      onClick={() => openExisting(t)}
                      style={{ cursor: 'pointer', background: selectedId === t._id ? 'var(--bg-hover)' : undefined }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{t.name}</div>
                      {t.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t.description}</div>}
                    </td>
                    <td style={{ fontSize: '0.82rem' }}>
                      {t.spreadsheetName
                        ? <>{t.spreadsheetName} <span style={{ color: 'var(--text-muted)' }}>→ {t.sheetName}</span></>
                        : <span style={{ color: 'var(--red, #ef4444)' }}>Not set</span>}
                    </td>
                    <td>{t.schema?.length || 0}</td>
                    <td>
                      {t.enabled
                        ? <span style={{ color: 'var(--green)', fontSize: '0.8rem' }}>● Active</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>○ Off</span>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {t.spreadsheetId && (
                        <a className="btn btn-sm" target="_blank" rel="noopener noreferrer"
                           href={`https://docs.google.com/spreadsheets/d/${t.spreadsheetId}`}
                           title="Open in Google Sheets">
                          <ExternalLink size={14} />
                        </a>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => remove(t)} style={{ marginLeft: 6 }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Editor */}
        {draft && (
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0 }}>{selectedId ? 'Edit Template' : 'New Template'}</h3>
              <button className="btn btn-sm" onClick={closeEditor}><X size={14} /></button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label className="form-label">Template Name</label>
                <input className="form-input" value={draft.name}
                       onChange={e => setDraft({ ...draft, name: e.target.value })}
                       placeholder="e.g. Supplier Invoice" />
              </div>

              <div>
                <label className="form-label">Description (optional)</label>
                <input className="form-input" value={draft.description}
                       onChange={e => setDraft({ ...draft, description: e.target.value })}
                       placeholder="What kind of document this template handles" />
              </div>

              <div>
                <label className="form-label">Classifier Hints</label>
                <input className="form-input" value={hintsText}
                       onChange={e => setHintsText(e.target.value)}
                       placeholder="TAX INVOICE, INVOICE NO, BILL TO" />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Comma-separated phrases the AI looks for to choose this template. The more match, the more likely this template wins.
                </div>
              </div>

              {/* Google Sheet destination */}
              <div style={{ padding: 12, background: 'var(--bg-hover)', borderRadius: 6 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '0.85rem' }}>
                  Save extracted rows to:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Google Spreadsheet</label>
                    <select className="form-input" value={draft.spreadsheetId}
                            onChange={e => onPickSpreadsheet(e.target.value)}
                            disabled={!googleConnected || loadingSheets}>
                      <option value="">{loadingSheets ? 'Loading…' : '— Select —'}</option>
                      {spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Sheet Tab</label>
                    <select className="form-input" value={draft.sheetName}
                            onChange={e => setDraft({ ...draft, sheetName: e.target.value })}
                            disabled={!draft.spreadsheetId || loadingTabs}>
                      <option value="">{loadingTabs ? 'Loading…' : '— Select —'}</option>
                      {tabs.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                    </select>
                  </div>
                </div>
                {draft.spreadsheetId && draft.sheetName && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6 }}>
                    Headers will be created automatically on the first row if the tab is empty.
                  </div>
                )}
              </div>

              {/* Google Contacts */}
              <div style={{ padding: 12, background: 'var(--bg-hover)', borderRadius: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={draft.saveToContacts}
                         onChange={e => setDraft({ ...draft, saveToContacts: e.target.checked })} />
                  <Contact size={15} /> Also save to Google Contacts
                </label>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  For business cards: works when this template has a <strong>name</strong> field plus a <strong>phone</strong> or <strong>email</strong> field. Contacts go under the label "AINA – Business Cards". Existing contacts with the same phone or email aren't duplicated. Other templates (invoices, vouchers) are skipped.
                </div>
                {draft.saveToContacts && googleConnected && !contactsAllowed && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(245,158,11,0.12)', color: '#b45309', fontSize: '0.78rem' }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>Your Google connection doesn't include Contacts yet. Reconnect once and allow "See, edit, download and delete your contacts".</span>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={reconnectGoogle}>Reconnect Google</button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={draft.enabled}
                         onChange={e => setDraft({ ...draft, enabled: e.target.checked })} />
                  Enabled
                </label>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Channel: <strong>{draft.channel}</strong>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label className="form-label" style={{ margin: 0 }}>Fields (Google Sheet columns)</label>
                  <button className="btn btn-sm" onClick={addField}><Plus size={13} /> Field</button>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {draft.schema.map((f, i) => (
                    <div key={i} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 110px auto auto',
                      gap: 8, alignItems: 'start',
                      padding: 8, border: '1px solid var(--border)', borderRadius: 6,
                    }}>
                      <input className="form-input" placeholder="key (e.g. invoiceNo)"
                             value={f.key} onChange={e => updateField(i, 'key', e.target.value)} />
                      <input className="form-input" placeholder="Column label"
                             value={f.label} onChange={e => updateField(i, 'label', e.target.value)} />
                      <select className="form-input" value={f.type}
                              onChange={e => updateField(i, 'type', e.target.value)}>
                        {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', paddingTop: 8 }}>
                        <input type="checkbox" checked={f.required}
                               onChange={e => updateField(i, 'required', e.target.checked)} />
                        req
                      </label>
                      <button className="btn btn-sm btn-danger" onClick={() => removeField(i)} style={{ marginTop: 2 }}>
                        <Trash2 size={13} />
                      </button>
                      <input className="form-input" placeholder="extra instruction for AI (optional)"
                             value={f.instruction || ''}
                             onChange={e => updateField(i, 'instruction', e.target.value)}
                             style={{ gridColumn: '1 / -1' }} />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
                <button className="btn" onClick={closeEditor}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>
                  <Save size={14} /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
